import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useCallback, useId, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ErrorState,
  FeatureErrorBoundary,
  useErrorMessage,
} from '@nucleus-commerce/admin-kit/errors';
import { MoneyInput, SummaryCard } from '@nucleus-commerce/admin-kit/forms';
import { PageContent } from '@nucleus-commerce/admin-kit/layout';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import { releaseSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import type { Product, StoreVariantPricing, Variant } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { toMajorString } from '@nucleus-commerce/core';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Form } from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { ChangeStockModal, useProductStock, useStockLocations } from '@/features/inventory';
import { MediaThumb, useMedia } from '@/features/media';
import {
  ProductScopeProvider,
  ScopeChip,
  SkuOriginChip,
  useDeleteVariant,
  useProduct,
  useVariantForm,
  useVariants,
  VariantBackorderField,
  VariantDeleteButton,
  VariantFulfilmentFields,
  VariantImagePicker,
  VariantMoneyField,
  VariantOptionValues,
  VariantPageFrame,
  VariantShippingFields,
  VariantStatusField,
  VariantTextField,
  VariantToggleField,
} from '@/features/products';
import { useStore } from '@/shared/store/store-context';

import { displayTitle } from '../api/mappers';
import { PriceSourceBadge } from '../components/price-source-badge';
import { StoreProductStatusBadge } from '../components/store-product-status-badge';
import { storeCatalogKeys } from '../hooks/store-catalog-keys';
import { useProductStoreCount } from '../hooks/use-product-store-count';
import { useStorePricingDrafts, type StorePricingDrafts } from '../hooks/use-store-pricing-drafts';
import { useStoreProduct } from '../hooks/use-store-product';
import {
  useVariantMarketDrafts,
  type VariantMarketDrafts,
} from '../hooks/use-variant-market-drafts';
import { isDraftDirty, priceSource, type PricingDraft } from '../schemas/pricing-rules';

const ROUTE = '/_authed/_active/s/$storeId/catalog/$storeProductId_/variants/$variantId' as const;
export const STORE_VARIANT_PAGE_PATH =
  '/s/$storeId/catalog/$storeProductId/variants/$variantId' as const;

const EDITED = 'border-primary bg-primary/5';
const GRID = 'grid items-start gap-4 sm:grid-cols-2';

function ReadField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1.5">
      <span className="text-sm font-medium">{label}</span>
      <div className="text-sm tabular-nums">{children}</div>
    </div>
  );
}

function SaveFailure({ error }: { error: unknown }) {
  const { t } = useTranslation('errors');
  const message = useErrorMessage();
  return (
    <Alert variant="destructive" role="alert">
      <AlertDescription>
        {message(error)}
        {isApiError(error) && error.requestId ? (
          <span className="block text-xs">
            {t('requestIdShort', { requestId: error.requestId })}
          </span>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}

/** The stock of this store: read-only, changed through its modal, detail on the inventory page. */
function StockField({
  storeId,
  productId,
  variant,
}: {
  storeId: string;
  productId: string;
  variant: Variant;
}) {
  const { t } = useTranslation('storeCatalog');
  const { can } = usePermissions();
  const readable = can('inventory.read');
  const stock = useProductStock(productId, readable);
  const locations = useStockLocations(storeId);
  const [changing, setChanging] = useState(false);
  if (!readable) return null;
  const only = locations.data?.length === 1 ? locations.data[0] : undefined;
  const available =
    stock.data?.find((row) => row.storeId === storeId && row.variantId === variant.id)?.available ??
    0;
  return (
    <div data-tour="catalog.variant.inventory">
      <ReadField
        label={
          only
            ? t('variantPage2.stockHereAt', { location: only.name })
            : t('variantPage2.stockHere')
        }
      >
        {variant.trackInventory ? (
          <span className="flex flex-wrap items-center gap-2">
            <Link
              to="/s/$storeId/inventory/$variantId"
              params={{ storeId, variantId: variant.id }}
              className="hover:underline"
            >
              {stock.isPending ? '…' : t('variantPage2.available', { count: available })}
            </Link>
            {can('inventory.write') ? (
              <Button type="button" size="xs" variant="outline" onClick={() => setChanging(true)}>
                {t('variantPage2.changeStock')}
              </Button>
            ) : null}
          </span>
        ) : (
          <span className="text-muted-foreground">{t('variantPage2.notTracked')}</span>
        )}
      </ReadField>
      {changing ? (
        <ChangeStockModal
          storeId={storeId}
          variant={{ id: variant.id, title: variant.title }}
          open={changing}
          onOpenChange={setChanging}
        />
      ) : null}
    </div>
  );
}

function StorePricingCard({
  variant,
  productId,
  storeId,
  pricing,
  markets,
}: {
  variant: Variant;
  productId: string;
  storeId: string;
  pricing: StorePricingDrafts;
  markets: VariantMarketDrafts;
}) {
  const { t } = useTranslation('storeCatalog');
  const format = useFormat();
  const id = useId();
  const row = pricing.rowOf(variant.id);

  if (pricing.isPending) {
    return (
      <SummaryCard title={t('variantPage2.storePricing')} badge={<ScopeChip scope="store" />}>
        <Skeleton className="h-40" />
      </SummaryCard>
    );
  }
  if (pricing.queryError || !row) {
    return (
      <SummaryCard title={t('variantPage2.storePricing')} badge={<ScopeChip scope="store" />}>
        <ErrorState error={pricing.queryError} onRetry={pricing.refetch} compact />
      </SummaryCard>
    );
  }
  const draft = pricing.draftFor(row);
  const set = (patch: Partial<typeof draft>) =>
    pricing.setDraft(variant.id, { ...draft, ...patch });
  const pays = shoppersPay(row, draft);
  const savedPrice = row.override?.price?.amount ?? null;
  const savedCompareAt = row.override?.compareAt?.amount ?? null;

  return (
    <SummaryCard title={t('variantPage2.storePricing')} badge={<ScopeChip scope="store" />}>
      <div className={GRID}>
        <div className="grid gap-1.5">
          <Label htmlFor={`${id}-price`}>
            {t('variantPage2.priceIn', { currency: pricing.currency })}
          </Label>
          <MoneyInput
            id={`${id}-price`}
            currency={pricing.currency}
            value={draft.price}
            disabled={!pricing.canWrite}
            placeholder={row.effective ? toMajorString(row.effective) : undefined}
            className={cn((draft.price?.amount ?? null) !== savedPrice && EDITED)}
            // The compare-at stays while the price is retyped; the save drops it without a price.
            onChange={(price) => set({ price })}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor={`${id}-compare`}>{t('variantPage2.compareAt')}</Label>
          <MoneyInput
            id={`${id}-compare`}
            currency={pricing.currency}
            value={draft.compareAt}
            disabled={!pricing.canWrite || !draft.price}
            placeholder="—"
            className={cn((draft.compareAt?.amount ?? null) !== savedCompareAt && EDITED)}
            onChange={(compareAt) => set({ compareAt })}
          />
        </div>
        {markets.markets.map((market) => {
          const marketDraft = markets.draftFor(market.id, variant.id);
          return (
            <div key={market.id} className="grid gap-1.5">
              <Label htmlFor={`${id}-${market.id}`}>
                {t('variantPage2.marketPrice', { name: market.name, currency: market.currency })}
              </Label>
              <MoneyInput
                id={`${id}-${market.id}`}
                currency={market.currency}
                value={marketDraft.price}
                disabled={!markets.canWrite}
                placeholder={t('variantPage2.automatic')}
                className={cn(markets.isEdited(market.id, variant.id) && EDITED)}
                onChange={(price) =>
                  markets.setDraft(market.id, variant.id, {
                    price,
                    compareAt: marketDraft.compareAt,
                  })
                }
              />
            </div>
          );
        })}
        <div className="grid gap-1.5">
          <Label htmlFor={`${id}-sku`}>{t('variantPage2.skuHere')}</Label>
          <Input
            id={`${id}-sku`}
            value={draft.skuOverride}
            disabled={!pricing.canWrite}
            placeholder={row.sku ?? ''}
            spellCheck={false}
            className={cn(
              'font-mono',
              draft.skuOverride.trim() !== (row.skuOverride ?? '') && EDITED,
            )}
            onChange={(event) => set({ skuOverride: event.target.value })}
          />
        </div>
        <ReadField label={t('variantPage2.shoppersPay')}>
          <span className="flex flex-wrap items-center gap-2">
            {pays.price ? (
              <span className="font-medium">
                {format.money(pays.price)}
                {pays.compareAt ? (
                  <span className="text-muted-foreground ml-1.5 text-xs line-through">
                    {format.money(pays.compareAt)}
                  </span>
                ) : null}
              </span>
            ) : (
              <span className="text-muted-foreground">
                {t('pricing.noRate', { currency: pricing.currency })}
              </span>
            )}
            <PriceSourceBadge
              source={pays.source}
              label={t(`variantPage2.storePrices.source.${pays.source}`)}
            />
          </span>
        </ReadField>
        <StockField storeId={storeId} productId={productId} variant={variant} />
        <div className="flex items-center gap-2 self-center">
          <Switch
            id={`${id}-sell`}
            checked={draft.isAvailable}
            disabled={!pricing.canWrite}
            onCheckedChange={(isAvailable) => set({ isAvailable })}
          />
          <Label htmlFor={`${id}-sell`} className="font-normal">
            {t('variantPage2.sellHere')}
          </Label>
        </div>
      </div>
    </SummaryCard>
  );
}

function SharedVariantCard({ product, variant }: { product: Product; variant: Variant }) {
  const { t } = useTranslation('storeCatalog');
  const { data: image } = useMedia(variant.mediaId);
  return (
    <SummaryCard title={t('variantPage2.variant')} badge={<ScopeChip scope="shared" />}>
      <div className={GRID}>
        <ReadField label={t('variantPage2.image')}>
          <MediaThumb media={image} className="size-11 rounded-lg border" />
        </ReadField>
        <span className="hidden sm:block" />
        {product.options.map((option) => {
          const code = variant.optionValues[option.key];
          const value = option.values.find((v) => v.value === code);
          return (
            <ReadField key={option.key} label={option.name}>
              {value?.label ?? code ?? '—'}
            </ReadField>
          );
        })}
      </div>
    </SummaryCard>
  );
}

function SharedIdentifiersCard({ product, variant }: { product: Product; variant: Variant }) {
  const { t } = useTranslation('storeCatalog');
  const none = t('variantPage2.none');
  const weight = variant.weight ?? product.weight;
  const min = variant.leadTimeDaysMin ?? product.leadTimeDaysMin;
  const max = variant.leadTimeDaysMax ?? product.leadTimeDaysMax;
  const days =
    min !== null && max !== null && min !== max
      ? t('variantPage2.productionDays', { min, max })
      : min !== null || max !== null
        ? t('variantPage2.productionDay', { days: min ?? max })
        : none;
  return (
    <SummaryCard title={t('variantPage2.identifiers')} badge={<ScopeChip scope="shared" />}>
      <div className={GRID}>
        <ReadField label={t('variantPage2.masterSku')}>
          <span className="font-mono">{variant.sku ?? none}</span>
        </ReadField>
        <ReadField label={t('variantPage2.barcode')}>{variant.barcode ?? none}</ReadField>
        <ReadField label={t('variantPage2.weight')}>
          {weight ? `${weight.value} ${weight.unit}` : none}
        </ReadField>
        <ReadField label={t('variantPage2.production')}>{days}</ReadField>
      </div>
    </SummaryCard>
  );
}

/**
 * The Shared cards for someone who may edit them: the master variant's own form, with its own save
 * bar entry beside the store's. A save here writes the master, so it reaches every store.
 */
function SharedVariantForm({
  product,
  variant,
  onSaved,
  onDelete,
}: {
  product: Product;
  variant: Variant;
  onSaved: () => void;
  onDelete: (variantId: string) => void;
}) {
  const { t } = useTranslation(['storeCatalog', 'products']);
  const { form, submit } = useVariantForm({ productId: product.id, variant, onSaved });
  const currency = variant.price.currency;
  const shared = <ScopeChip scope="shared" />;
  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="flex min-w-0 flex-col gap-4">
        {form.formState.errors.root?.server ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
          </Alert>
        ) : null}

        <SummaryCard title={t('storeCatalog:variantPage2.variant')} badge={shared}>
          <div className={GRID}>
            <div className="grid gap-1.5">
              <span className="text-sm font-medium">{t('storeCatalog:variantPage2.image')}</span>
              <VariantImagePicker product={product} variant={variant} />
            </div>
            <VariantStatusField />
            <VariantOptionValues product={product} variant={variant} />
          </div>
        </SummaryCard>

        <SummaryCard title={t('storeCatalog:variantPage2.identifiers')} badge={shared}>
          <div className={GRID}>
            <VariantTextField
              name="sku"
              label={t('storeCatalog:variantPage2.masterSku')}
              extra={
                variant.sku ? (
                  <SkuOriginChip origin={variant.skuIsManual ? 'manual' : 'generated'} />
                ) : null
              }
            />
            <VariantTextField name="barcode" label={t('storeCatalog:variantPage2.barcode')} />
            <VariantMoneyField
              name="cost"
              label={t('products:variantPage.costPerItem')}
              hint={t('products:variantPage.costHint')}
              currency={currency}
            />
            <VariantTextField name="taxCode" label={t('products:variants.sheet.taxCode')} />
            <VariantToggleField name="taxable" label={t('products:variantPage.chargeTax')} />
            <VariantToggleField
              name="trackInventory"
              label={t('products:variantPage2.trackStock')}
            />
            <VariantBackorderField />
            <VariantToggleField
              name="requiresShipping"
              label={t('products:variantPage.physical')}
            />
          </div>
          <VariantShippingFields />
          <VariantFulfilmentFields />
        </SummaryCard>

        <VariantDeleteButton
          variant={variant}
          description={t('products:variantPage2.deleteEveryStore', { variant: variant.title })}
          onDelete={onDelete}
        />
      </form>
    </Form>
  );
}

/**
 * What the default market would charge with the unsaved store price: a store price is charged as
 * entered and a market's fixed price wins over it. Clearing one falls back to a base price only
 * this page can read when no conversion is involved; otherwise the saved price stands until Save.
 */
function shoppersPay(row: StoreVariantPricing, draft: PricingDraft) {
  const saved = {
    price: row.effective,
    compareAt: row.effectiveCompareAt,
    source: priceSource(row),
  };
  if (row.source === 'price_list' || !isDraftDirty(draft, row)) return saved;
  if (draft.price) {
    return { price: draft.price, compareAt: draft.compareAt, source: 'store' as const };
  }
  return row.override && row.base.currency === row.effective?.currency
    ? { price: row.base, compareAt: null, source: 'base' as const }
    : saved;
}

function StoreVariant() {
  const { t } = useTranslation('products');
  const store = useStore();
  const { can } = usePermissions();
  const stockReadable = can('inventory.read');
  const canWrite = can('products.write');
  const { storeId, storeProductId, variantId } = useParams({ from: ROUTE });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const row = useStoreProduct(storeId, storeProductId);
  const productId = row.data?.productId ?? '';
  const product = useProduct(productId);
  const variants = useVariants(productId);
  const remove = useDeleteVariant(productId);
  const pricing = useStorePricingDrafts(storeId, storeProductId, store.defaultCurrency);
  const markets = useVariantMarketDrafts(storeId, storeProductId, productId);
  const storeCount = useProductStoreCount(productId);
  const stock = useProductStock(productId, stockReadable && productId !== '');
  const available = useMemo(
    () =>
      new Map(
        (stock.data ?? [])
          .filter((entry) => entry.storeId === storeId)
          .map((entry) => [entry.variantId, entry.available]),
      ),
    [stock.data, storeId],
  );
  const linkTo = useCallback(
    (id: string) => ({
      to: STORE_VARIANT_PAGE_PATH,
      params: { storeId, storeProductId, variantId: id },
    }),
    [storeId, storeProductId],
  );

  const loading = (
    <PageContent>
      <Skeleton className="h-10 w-72" />
      <div className="grid gap-6 lg:grid-cols-[1fr_2fr]">
        <Skeleton className="h-80" />
        <Skeleton className="h-[32rem]" />
      </div>
    </PageContent>
  );
  if (row.isPending || product.isPending || variants.isPending) return loading;
  const error = row.error ?? product.error ?? variants.error;
  if (error || !row.data || !product.data || !variants.data) {
    return <ErrorState error={error} onRetry={() => void row.refetch()} />;
  }
  const variant = variants.data.find((v) => v.id === variantId);
  if (!variant) {
    // Deleting it takes the row out of the cache before the page has left.
    if (remove.isPending || remove.isSuccess) return loading;
    return (
      <PageContent>
        <p className="text-muted-foreground py-10 text-center text-sm">
          {t('variantPage.notFound')}
        </p>
      </PageContent>
    );
  }
  const back = {
    to: '/s/$storeId/catalog/$storeProductId',
    params: { storeId, storeProductId },
    hash: 'product-variants',
  } as const;
  const refreshPricing = () => {
    void queryClient.invalidateQueries({
      queryKey: storeCatalogKeys.pricing(storeId, storeProductId),
    });
  };
  return (
    <ProductScopeProvider
      value={{
        shared: true,
        readOnly: !canWrite,
        productId: product.data.id,
        storeName: store.name,
        storeCount: Math.max(storeCount, 1),
      }}
    >
      <VariantPageFrame
        product={product.data}
        variant={variant}
        variants={variants.data}
        backTo={back}
        backLabel={displayTitle(row.data)}
        linkTo={linkTo}
        railMeta={(entry) => ({
          price: pricing.rowOf(entry.id)?.effective ?? null,
          stock: stockReadable && entry.trackInventory ? (available.get(entry.id) ?? 0) : null,
        })}
        railHeading={{
          title: displayTitle(row.data),
          mediaId: row.data.featuredMediaId ?? product.data.featuredMediaId,
          badge: <StoreProductStatusBadge status={row.data.status} />,
        }}
      >
        <div className="flex min-w-0 flex-col gap-4">
          {pricing.saveError ? <SaveFailure error={pricing.saveError} /> : null}
          {markets.saveError ? <SaveFailure error={markets.saveError} /> : null}
          <StorePricingCard
            key={`pricing-${variant.id}`}
            variant={variant}
            productId={product.data.id}
            storeId={storeId}
            pricing={pricing}
            markets={markets}
          />
          {canWrite ? (
            <SharedVariantForm
              key={`shared-${variant.id}`}
              product={product.data}
              variant={variant}
              // The store's rows carry the master SKU and base price.
              onSaved={refreshPricing}
              onDelete={(id) =>
                remove.mutate(id, {
                  onSuccess: () => {
                    // The variant is gone, so unsaved edits to it must not hold the person here.
                    releaseSaveBar();
                    refreshPricing();
                    void navigate(back);
                  },
                })
              }
            />
          ) : (
            <>
              <SharedVariantCard product={product.data} variant={variant} />
              <SharedIdentifiersCard product={product.data} variant={variant} />
            </>
          )}
        </div>
      </VariantPageFrame>
    </ProductScopeProvider>
  );
}

/** A variant opened from a store's product: this store's own fields, then the shared master ones. */
export function StoreVariantPage() {
  return (
    <FeatureErrorBoundary name="storeCatalog.variant">
      <StoreVariant />
    </FeatureErrorBoundary>
  );
}
